import React from 'react';
import { Text, Toggle, useReadOnly } from '@spark';
import FieldCard from '@/shared/components/FieldCard';
import { countTokenUsage, EXAMPLE_TOKEN } from '../utils/promptTemplate';

const toSampleText = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

/** Full-skeleton dataset cards shown while the sample record loads. */
const LOADING_CARD_COUNT = 3;

/**
 * Additional fields rail (Figma .ModelSetup-AdditionalFieldsRail): the same
 * field cards as the Map prompt template modal's Available fields panel
 * (shared FieldCard), split into Custom fields (the `example` model input)
 * and Dataset columns (extra dataset fields insertable as `{{field}}`
 * context tokens).
 */
const AdditionalFieldsRail = ({
    fields = {},
    promptTemplate = '',
    /** Sample value of the `example` model input (the primary field). */
    exampleValue = '',
    enabled = false,
    onEnabledChange,
    onInsert,
    disabled = false,
    loading = false,
}) => {
    const { readOnly } = useReadOnly();
    const fieldNames = Object.keys(fields || {});
    const showLoadingCards = loading && fieldNames.length === 0;

    const fieldCard = ({ name, value, variant, isPrimary = false, skeletonIndex = 0 }) => {
        const usageCount = countTokenUsage(promptTemplate, name);
        const used = usageCount > 0;
        return (
            <FieldCard
                key={name}
                name={name}
                value={toSampleText(value)}
                typeIconSlug={typeof value === 'string' ? 'text' : 'code-dots'}
                variant={variant}
                valueLoading={loading}
                skeletonIndex={skeletonIndex}
                occurrences={usageCount}
                isMapped={used}
                isPrimary={isPrimary}
                interactive={!readOnly && !disabled && !used && !loading}
                tooltip="Click to insert into the prompt"
                onClick={() => onInsert?.(name)}
            />
        );
    };

    return (
        <div className="model-setup-fields-rail fs-unmask">
            <div className="model-setup-fields-rail__head">
                <div className="model-setup-fields-rail__copy">
                    <Text variant="base-medium" color="primary" as="div">
                        Additional fields
                    </Text>
                    <Text variant="detail-regular" color="secondary" as="div">
                        {enabled
                            ? 'Add extra dataset fields as context.'
                            : 'Show extra dataset fields to add as context'}
                    </Text>
                </div>
                <Toggle
                    size="small"
                    active={enabled}
                    disabled={disabled}
                    aria-label="Additional fields"
                    onChange={(e) => onEnabledChange?.(e.target.checked)}
                />
            </div>
            {enabled && (
                <>
                    <section className="model-setup-fields-rail__section">
                        <h5 className="model-setup-fields-rail__section-title">
                            Custom fields (1)
                        </h5>
                        {fieldCard({
                            name: EXAMPLE_TOKEN,
                            value: exampleValue,
                            variant: 'custom',
                            isPrimary: true,
                        })}
                    </section>
                    <section className="model-setup-fields-rail__section">
                        <h5 className="model-setup-fields-rail__section-title">
                            Dataset columns ({fieldNames.length})
                        </h5>
                        {fieldNames.length === 0 && !showLoadingCards ? (
                            <Text variant="detail-regular" color="secondary" as="div">
                                No extra dataset fields available yet.
                            </Text>
                        ) : (
                            <div className="model-setup-fields-rail__columns">
                                {showLoadingCards
                                    ? Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
                                        <FieldCard
                                            key={`loading-${index}`}
                                            name=""
                                            typeIconSlug="code-dots"
                                            loading
                                            skeletonIndex={index}
                                            interactive={false}
                                        />
                                    ))
                                    : fieldNames.map((name, index) =>
                                        fieldCard({
                                            name,
                                            value: fields[name],
                                            variant: 'column',
                                            skeletonIndex: index,
                                        })
                                    )}
                            </div>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default AdditionalFieldsRail;
